// Branch forecast = hub volume × branch share. Branch volume is DOWNSTREAM of
// the hub (customers inject into a hub, the hub sorts and ships to its branches),
// so any change to the hub's forecast automatically flows to its branches.
//
// A branch's share of its hub is DAY-OF-WEEK specific (matching LogiCast): a
// branch can take a bigger slice on Mondays than Saturdays. For each weekday the
// share is derived with this priority:
//   1. ACTUAL  — the branch's fraction of its hub's volume on THAT weekday from
//      imported branch actuals over the window. If a weekday has no actuals, fall
//      back to the branch's OVERALL actual share (so every weekday still sums to 1).
//   2. MANUAL  — the configured `sharePct` (same every weekday), normalized.
//   3. EQUAL   — split evenly across the hub's active branches.
// Shares are normalized to sum to 1 within each hub, per weekday. This module is pure.

import type { Branch } from "../types";

export type ShareSource = "actual" | "manual" | "equal";

export interface BranchShare {
  branchId: string;
  hubId: string;
  shareByDow: number[]; // length 7, JS getDay() (0=Sun..6=Sat); each weekday sums to 1 within the hub
  source: ShareSource;
}

/** Minimal shape needed from a branch actual (structurally compatible with BranchActual). */
interface BranchActualLike {
  branchId: string;
  date: string; // YYYY-MM-DD
  actualVolume: number;
}

const dowOf = (date: string) => new Date(date + "T12:00:00").getDay();

/**
 * Per-branch day-of-week shares of its hub, by branch id. Only active branches
 * are included; within each hub the shares sum to 1 for every weekday.
 */
export function branchShares(branches: Branch[], actuals: BranchActualLike[]): Map<string, BranchShare> {
  // Per branch: overall actual total + per-weekday actual totals over the window.
  const overall = new Map<string, number>();
  const perDow = new Map<string, number[]>();
  for (const a of actuals) {
    overall.set(a.branchId, (overall.get(a.branchId) ?? 0) + a.actualVolume);
    let arr = perDow.get(a.branchId);
    if (!arr) {
      arr = new Array<number>(7).fill(0);
      perDow.set(a.branchId, arr);
    }
    arr[dowOf(a.date)] += a.actualVolume;
  }

  const byHub = new Map<string, Branch[]>();
  for (const b of branches) {
    if (!b.active) continue;
    const arr = byHub.get(b.hubId);
    if (arr) arr.push(b);
    else byHub.set(b.hubId, [b]);
  }

  const out = new Map<string, BranchShare>();
  for (const [hubId, list] of byHub) {
    const overallTot = list.reduce((s, b) => s + (overall.get(b.id) ?? 0), 0);
    const dowTot = new Array<number>(7).fill(0);
    for (const b of list) {
      const arr = perDow.get(b.id);
      if (arr) for (let d = 0; d < 7; d++) dowTot[d] += arr[d];
    }
    const shareTot = list.reduce((s, b) => s + (b.sharePct ?? 0), 0);

    let source: ShareSource;
    let shareFor: (b: Branch, dow: number) => number;
    if (overallTot > 0) {
      source = "actual";
      shareFor = (b, dow) =>
        dowTot[dow] > 0
          ? (perDow.get(b.id)?.[dow] ?? 0) / dowTot[dow] // that weekday's actual share
          : (overall.get(b.id) ?? 0) / overallTot; // no data that weekday → overall actual share
    } else if (shareTot > 0) {
      source = "manual";
      shareFor = (b) => (b.sharePct ?? 0) / shareTot;
    } else {
      source = "equal";
      shareFor = () => 1 / list.length;
    }

    for (const b of list) {
      const shareByDow = Array.from({ length: 7 }, (_, d) => shareFor(b, d));
      out.set(b.id, { branchId: b.id, hubId, shareByDow, source });
    }
  }
  return out;
}

/** A branch's forecast for a day = the hub's volume that day × the branch's share for that weekday. */
export function forecastBranchDay(hubVolume: number, share: number): number {
  return hubVolume * share;
}
